import { useStore } from '@/store';
import { useNotify, useRecentFiles } from '@/hooks';
import { useFileToTextParser } from '@/api';
import { useEffect, useState } from 'react';
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_FILE_MIME_TYPES,
  MIME_TYPE_TO_EXTENSION,
} from '@/config';
import { t } from 'i18next';
import { useSetupStore } from '../../store';
import { DirtyType } from '@/types';

export const useFileSelect = () => {
  /* Global state */
  const [hasGlobalFile, setStoreFile, setStoreFiles, setTextContent, variant, templateId] =
    useStore(({ config: c }) => [
      Boolean(c.file),
      c.setFile,
      c.setFiles,
      c.setTextContent,
      c.getVariant(),
      c.getId(),
    ]);
  /* Context state */
  const addDirtyAll = useStore(({ entries: e }) => e.addDirtyAll);
  const [step, setCurrentStep] = useSetupStore((s) => [
    s.currentStep,
    s.setCurrentStep,
  ]);
  /* Local state */
  const [file, setFile] = useState<File | undefined>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [parseTrigger, setParseTrigger] = useState(0);
  const [notification, setNotification] = useState<string | undefined>(
    undefined,
  );
  /* Hooks */
  const { close, warn, loading, error: notifyError } = useNotify();
  const {
    recentFiles,
    addRecentFile,
    loadRecentFile,
    downloadRecentFile,
    removeRecentFile,
  } = useRecentFiles(templateId);
  /* Queries */
  const { data, error, isError, dataUpdatedAt } = useFileToTextParser(
    file,
    parseTrigger,
  );

  // Update recent files when a new file is selected
  const updateRecentFiles = async (file: File) => {
    if (data === undefined) {
      return;
    }
    await addRecentFile(file);
  };

  const handleRecentFileSelect = async (fileId: string) => {
    const selectedFileResult = await loadRecentFile(fileId);
    if (selectedFileResult.isErr()) {
      return;
    }

    setFile(selectedFileResult.value);
    setParseTrigger((prev) => prev + 1);
  };

  const handleRecentFileDownload = async (fileId: string) => {
    await downloadRecentFile(fileId);
  };

  const handleRecentFileDelete = async (fileId: string) => {
    await removeRecentFile(fileId);
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (file === undefined) {
      return;
    }

    const id = loading(t('message.loading.file'));
    setNotification(id);
  }, [file]);

  useEffect(() => {
    if (data === undefined) {
      return;
    }
    if (dataUpdatedAt === 0) {
      return;
    }

    setTextContent(data);
    addDirtyAll(DirtyType.Template);
  }, [data, dataUpdatedAt]);

  useEffect(() => {
    if (!isError) {
      return;
    }

    if (notification) {
      close(notification);
      setNotification(undefined);
    }

    notifyError(t('message.error.fileParseFailed'));
  }, [isError, error]);

  useEffect(() => {
    if (notification === undefined) {
      return;
    }
    if (data === undefined) {
      return;
    }
    if (dataUpdatedAt === 0) {
      return;
    }

    close(notification);
    setNotification(undefined);

    // Persist selected file globally and update recents so header can proceed
    if (file) {
      setStoreFile(file);
      updateRecentFiles(file);
    }
  }, [data, dataUpdatedAt, notification]);

  /**
   * Handlers
   */
  // When the user clicks the next button, advance if a file is present globally.
  const progressSetup = () => {
    if (!hasGlobalFile) {
      warn(t('message.warning.selectFile'));
      return;
    }
    setCurrentStep(step + 1);
  };

  const getAcceptedFileExtensions = () => {
    // Return both MIME types and file extensions
    // MIME types for Mantine Dropzone validation, extensions for native file input
    const mimeTypes = ACCEPTED_FILE_MIME_TYPES[variant];
    const extensions = ACCEPTED_FILE_EXTENSIONS[variant];
    return [...mimeTypes, ...extensions];
  };

  const getAcceptedFileExtensionsForInput = () => {
    // Native HTML file input accepts both MIME types and file extensions
    // Include both for better browser compatibility
    const mimeTypes = ACCEPTED_FILE_MIME_TYPES[variant];
    const extensions = ACCEPTED_FILE_EXTENSIONS[variant];
    return [...mimeTypes, ...extensions].join(',');
  };

  const getAcceptedFileExtensionsDisplay = () => {
    const mimeTypes = ACCEPTED_FILE_MIME_TYPES[variant];
    const extensions = mimeTypes.map(
      (mimeType) => MIME_TYPE_TO_EXTENSION[mimeType] || mimeType,
    );
    return Array.from(new Set(extensions));
  };

  const handleFileSelect = (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    const merged = [...selectedFiles, ...newFiles];
    setSelectedFiles(merged);
    setStoreFiles(merged);
    setFile(merged[0]);
    setParseTrigger((prev) => prev + 1);
    // Add all selected files to recent so the user can switch between them
    newFiles.forEach((f) => addRecentFile(f));
  };

  const handleRemoveFile = (index: number) => {
    const updated = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updated);
    setStoreFiles(updated);
    if (updated.length > 0) {
      setFile(updated[0]);
      setParseTrigger((prev) => prev + 1);
    } else {
      setFile(undefined);
    }
  };

  return {
    file,
    selectedFiles,
    handleFileSelect,
    handleRemoveFile,
    progressSetup,
    disableNext: !hasGlobalFile,
    getAcceptedFileExtensions,
    getAcceptedFileExtensionsForInput,
    getAcceptedFileExtensionsDisplay,
    recentFiles,
    handleRecentFileSelect,
    handleRecentFileDownload,
    handleRecentFileDelete,
  };
};
